export interface CategorySection {
  heading: string;
  paragraph: string;
  bullets?: string[];
}

export interface CategoryFaq {
  q: string;
  a: string;
}

export interface CategoryContent {
  title: string;
  metaDescription: string;
  excerpt: string;
  intro: string;
  sections: CategorySection[];
  faq: CategoryFaq[];
  conclusion: string;
}

export interface CategoryArticle {
  slug: string;
  content: Record<string, CategoryContent>;
}

export const categoryArticlesA: CategoryArticle[] = [
  {
    slug: 'shoes',
    content: {
      en: {
        title: 'USFANS Spreadsheet Shoes: What to Look For Before You Buy',
        metaDescription: 'A focused guide to the shoes category in the USFANS spreadsheet — what to check about sizing, materials and authenticity before ordering sneakers.',
        excerpt: 'What to check about sizing, materials and authenticity before buying shoes.',
        intro: 'Shoes are usually the most popular category in any USFANS-style spreadsheet, and also the one where small details matter most. A little extra checking up front can save you from a costly sizing or quality mistake.',
        sections: [
          {
            heading: 'What to Check Before Buying Shoes',
            paragraph: 'Sneakers and shoes have more moving parts than most clothing, so a few specific checks go a long way.',
            bullets: [
              'Confirm sizing in your usual system (US, EU or UK)',
              'Ask about the exact material and whether it matches the photos',
              'Look for close-up QC photos of logos and stitching',
            ],
          },
          {
            heading: 'Common Shoe Buying Mistakes',
            paragraph: 'Most shoe mistakes come down to rushing. Taking a moment to verify sizing and condition prevents the two biggest regrets: the wrong fit and a poor finish.',
          },
        ],
        faq: [
          { q: 'How do I know my shoe size?', a: 'Confirm the sizing system the seller uses and compare it against a pair you already own, rather than guessing from a chart alone.' },
          { q: 'Should I ask for QC photos of shoes?', a: 'Yes. Clear close-ups of logos, stitching and the sole are the most useful way to spot problems before shipping.' },
          { q: 'Are the shoes authentic?', a: 'We are an independent resource and cannot verify authenticity. Check the listing details, compare photos and ask the seller direct questions.' },
        ],
        conclusion: 'Take a little time to confirm sizing, materials and condition, and the shoes category becomes much easier to navigate with confidence.',
      },
      es: {
        title: 'Zapatos en el spreadsheet de USFANS: qué mirar antes de comprar',
        metaDescription: 'Una guía centrada en la categoría de zapatos del spreadsheet de USFANS: qué revisar sobre tallas, materiales y autenticidad antes de pedir.',
        excerpt: 'Qué revisar sobre tallas, materiales y autenticidad antes de comprar zapatos.',
        intro: 'Los zapatos suelen ser la categoría más popular en cualquier spreadsheet tipo USFANS, y también donde más importan los pequeños detalles. Revisar un poco más al principio te evita errores costosos de talla o calidad.',
        sections: [
          {
            heading: 'Qué revisar antes de comprar zapatos',
            paragraph: 'Las zapatillas tienen más piezas que la mayoría de la ropa, así que unas comprobaciones concretas ayudan mucho.',
            bullets: [
              'Confirma la talla en tu sistema habitual (US, EU o UK)',
              'Pregunta por el material exacto y si coincide con las fotos',
              'Busca fotos de control de calidad cercanas de logos y costuras',
            ],
          },
          {
            heading: 'Errores comunes al comprar zapatos',
            paragraph: 'La mayoría de errores con zapatos vienen de las prisas. Tomarte un momento para verificar talla y estado evita los dos arrepentimientos más grandes: la talla equivocada y un mal acabado.',
          },
        ],
        faq: [
          { q: '¿Cómo sé mi talla de zapato?', a: 'Confirma el sistema de tallas que usa el vendedor y compáralo con un par que ya tengas, en lugar de adivinar solo con una tabla.' },
          { q: '¿Debo pedir fotos de control de calidad de los zapatos?', a: 'Sí. Los primeros planos claros de logos, costuras y suela son la forma más útil de detectar problemas antes del envío.' },
          { q: '¿Son auténticos los zapatos?', a: 'Somos un recurso independiente y no podemos verificar la autenticidad. Revisa los detalles del anuncio, compara fotos y pregunta directamente al vendedor.' },
        ],
        conclusion: 'Dedica un poco de tiempo a confirmar talla, materiales y estado, y la categoría de zapatos se vuelve mucho más fácil de navegar con confianza.',
      },
      fr: {
        title: 'Chaussures dans le spreadsheet USFANS : quoi vérifier avant d\u2019acheter',
        metaDescription: 'Un guide ciblé sur la catégorie chaussures du spreadsheet USFANS : quoi vérifier sur les tailles, les matières et l\u2019authenticité avant de commander.',
        excerpt: 'Quoi vérifier sur les tailles, les matières et l\u2019authenticité avant d\u2019acheter des chaussures.',
        intro: 'Les chaussures sont souvent la catégorie la plus populaire dans un spreadsheet type USFANS, et aussi celle où les petits détails comptent le plus. Vérifier un peu plus au départ vous évite des erreurs coûteuses de taille ou de qualité.',
        sections: [
          {
            heading: 'Quoi vérifier avant d\u2019acheter des chaussures',
            paragraph: 'Les sneakers ont plus de pièces que la plupart des vêtements, donc quelques vérifications précises aident beaucoup.',
            bullets: [
              'Confirmez la taille dans votre système habituel (US, EU ou UK)',
              'Demandez la matière exacte et vérifiez qu\u2019elle correspond aux photos',
              'Cherchez des photos QC en gros plan des logos et coutures',
            ],
          },
          {
            heading: 'Erreurs courantes à l\u2019achat de chaussures',
            paragraph: 'La plupart des erreurs de chaussures viennent de la précipitation. Prendre un moment pour vérifier la taille et l\u2019état évite les deux plus grands regrets : la mauvaise pointure et une mauvaise finition.',
          },
        ],
        faq: [
          { q: 'Comment connaître ma pointure ?', a: 'Confirmez le système de tailles utilisé par le vendeur et comparez avec une paire que vous possédez déjà, plutôt que de deviner avec un simple tableau.' },
          { q: 'Dois-je demander des photos QC des chaussures ?', a: 'Oui. Des gros plans clairs des logos, coutures et de la semelle sont le meilleur moyen de repérer les problèmes avant l\u2019expédition.' },
          { q: 'Les chaussures sont-elles authentiques ?', a: 'Nous sommes une ressource indépendante et ne pouvons pas vérifier l\u2019authenticité. Vérifiez les détails de l\u2019annonce, comparez les photos et posez des questions directes au vendeur.' },
        ],
        conclusion: 'Prenez un peu de temps pour confirmer la taille, les matières et l\u2019état, et la catégorie chaussures devient bien plus facile à naviguer avec confiance.',
      },
      pl: {
        title: 'Buty w arkuszu USFANS: co sprawdzić przed zakupem',
        metaDescription: 'Przewodnik skupiony na kategorii butów w arkuszu USFANS — co sprawdzić w kwestii rozmiarów, materiałów i autentyczności przed zamówieniem.',
        excerpt: 'Co sprawdzić w kwestii rozmiarów, materiałów i autentyczności przed zakupem butów.',
        intro: 'Buty to zwykle najpopularniejsza kategoria w każdym arkuszu typu USFANS, a zarazem ta, w której drobne detale mają największe znaczenie. Odrobina dodatkowego sprawdzenia na początku oszczędza kosztownych błędów rozmiaru lub jakości.',
        sections: [
          {
            heading: 'Co sprawdzić przed zakupem butów',
            paragraph: 'Sneakersy mają więcej elementów niż większość ubrań, więc kilka konkretnych kontroli bardzo pomaga.',
            bullets: [
              'Potwierdź rozmiar w swoim systemie (US, EU lub UK)',
              'Zapytaj o dokładny materiał i czy zgadza się ze zdjęciami',
              'Szukaj zbliżeń QC na logo i szwy',
            ],
          },
          {
            heading: 'Typowe błędy przy kupowaniu butów',
            paragraph: 'Większość błędów z butami wynika z pośpiechu. Poświęcenie chwili na weryfikację rozmiaru i stanu pozwala uniknąć dwóch największych żalów: złego rozmiaru i słabego wykończenia.',
          },
        ],
        faq: [
          { q: 'Skąd mam znać swój rozmiar buta?', a: 'Potwierdź system rozmiarów używany przez sprzedawcę i porównaj go z parą, którą już masz, zamiast zgadywać wyłącznie z tabeli.' },
          { q: 'Czy prosić o zdjęcia QC butów?', a: 'Tak. Wyraźne zbliżenia na logo, szwy i podeszwę to najbardziej przydatny sposób wykrywania problemów przed wysyłką.' },
          { q: 'Czy buty są autentyczne?', a: 'Jesteśmy niezależnym zasobem i nie możemy zweryfikować autentyczności. Sprawdź szczegóły ogłoszenia, porównaj zdjęcia i zadaj sprzedawcy bezpośrednie pytania.' },
        ],
        conclusion: 'Poświęć trochę czasu na potwierdzenie rozmiaru, materiałów i stanu, a kategoria butów stanie się znacznie łatwiejsza do pewnego przeglądania.',
      },
    },
  },
  {
    slug: 'hoodies',
    content: {
      en: {
        title: 'USFANS Spreadsheet Hoodies & Sweaters: A Quick Buying Guide',
        metaDescription: 'A focused guide to the hoodies and sweaters category in the USFANS spreadsheet — material, fit and the details worth checking before ordering.',
        excerpt: 'What to check about material, fit and details before buying hoodies.',
        intro: 'Hoodies and sweaters are all about comfort, but comfort depends on details you cannot always see in a single photo — like material weight, fit and print quality. A few targeted questions make a big difference.',
        sections: [
          {
            heading: 'What to Check Before Buying Hoodies',
            paragraph: 'Fabric and fit are the two things that decide whether a hoodie becomes a favorite or stays in the drawer.',
            bullets: [
              'Ask about the fabric weight and whether it is cotton or a blend',
              'Confirm the fit — relaxed, oversized or true to size',
              'Check print or embroidery quality in close-up photos',
            ],
          },
          {
            heading: 'Hoodie Details That Matter',
            paragraph: 'Things like the thickness of the drawstrings, the lining and the stitching around the pocket are small but tell you a lot about overall quality.',
          },
        ],
        faq: [
          { q: 'What material is the hoodie made of?', a: 'Ask the seller directly about the fabric, since listings are not always specific. Cotton, fleece and blends all fit and wash differently.' },
          { q: 'How do I choose the right fit?', a: 'Confirm whether the hoodie runs true to size, oversized or slim, and compare measurements against a hoodie you already own.' },
          { q: 'Should I check the print quality?', a: 'Yes. Ask for a close-up of any print or embroidery to confirm it looks clean before you order.' },
        ],
        conclusion: 'Confirm the fabric, fit and finishing details, and you will get a hoodie you actually want to wear, not one that just looked good in photos.',
      },
      es: {
        title: 'Sudaderas y jerséis en el spreadsheet de USFANS: guía rápida de compra',
        metaDescription: 'Una guía centrada en la categoría de sudaderas y jerséis del spreadsheet de USFANS: material, tallaje y detalles que vale la pena revisar antes de pedir.',
        excerpt: 'Qué revisar sobre material, tallaje y detalles antes de comprar sudaderas.',
        intro: 'Las sudaderas y jerséis son pura comodidad, pero la comodidad depende de detalles que no siempre se ven en una foto — como el peso del tejido, el ajuste y la calidad del estampado. Unas preguntas concretas marcan la diferencia.',
        sections: [
          {
            heading: 'Qué revisar antes de comprar sudaderas',
            paragraph: 'El tejido y el ajuste son las dos cosas que deciden si una sudadera se convierte en favorita o se queda en el cajón.',
            bullets: [
              'Pregunta por el peso del tejido y si es algodón o mezcla',
              'Confirma el ajuste — relajado, holgado o talla real',
              'Revisa la calidad del estampado o bordado en fotos cercanas',
            ],
          },
          {
            heading: 'Detalles de la sudadera que importan',
            paragraph: 'Cosas como el grosor de los cordones, el forro y las costuras del bolsillo son pequeñas, pero dicen mucho de la calidad general.',
          },
        ],
        faq: [
          { q: '¿De qué material es la sudadera?', a: 'Pregunta directamente al vendedor por el tejido, ya que los anuncios no siempre son específicos. El algodón, el forro polar y las mezclas sientan y lavan distinto.' },
          { q: '¿Cómo elijo el ajuste correcto?', a: 'Confirma si la sudadera es de talla real, holgada o ajustada, y compara las medidas con una sudadera que ya tengas.' },
          { q: '¿Debo revisar la calidad del estampado?', a: 'Sí. Pide un primer plano de cualquier estampado o bordado para confirmar que se ve limpio antes de pedir.' },
        ],
        conclusion: 'Confirma el tejido, el ajuste y los detalles de acabado, y conseguirás una sudadera que de verdad quieras usar, no una que solo se veía bien en fotos.',
      },
      fr: {
        title: 'Hoodies et pulls dans le spreadsheet USFANS : guide d\u2019achat rapide',
        metaDescription: 'Un guide ciblé sur la catégorie hoodies et pulls du spreadsheet USFANS : matière, coupe et détails à vérifier avant de commander.',
        excerpt: 'Quoi vérifier sur la matière, la coupe et les détails avant d\u2019acheter des hoodies.',
        intro: 'Les hoodies et pulls sont une affaire de confort, mais le confort dépend de détails qu\u2019on ne voit pas toujours sur une seule photo — comme le poids du tissu, la coupe et la qualité de l\u2019impression. Quelques questions ciblées font une vraie différence.',
        sections: [
          {
            heading: 'Quoi vérifier avant d\u2019acheter des hoodies',
            paragraph: 'Le tissu et la coupe sont les deux choses qui décident si un hoodie devient un favori ou reste au placard.',
            bullets: [
              'Demandez le poids du tissu et s\u2019il est en coton ou en mélange',
              'Confirmez la coupe — décontractée, oversize ou taille normale',
              'Vérifiez la qualité de l\u2019impression ou de la broderie en gros plan',
            ],
          },
          {
            heading: 'Les détails qui comptent sur un hoodie',
            paragraph: 'Des choses comme l\u2019épaisseur des cordons, la doublure et les coutures autour de la poche sont petites, mais en disent long sur la qualité générale.',
          },
        ],
        faq: [
          { q: 'De quelle matière est le hoodie ?', a: 'Demandez directement au vendeur, car les annonces ne sont pas toujours précises. Le coton, le molleton et les mélanges se portent et se lavent différemment.' },
          { q: 'Comment choisir la bonne coupe ?', a: 'Confirmez si le hoodie taille normal, oversize ou slim, et comparez les mesures avec un hoodie que vous possédez déjà.' },
          { q: 'Dois-je vérifier la qualité de l\u2019impression ?', a: 'Oui. Demandez un gros plan de toute impression ou broderie pour confirmer qu\u2019elle est propre avant de commander.' },
        ],
        conclusion: 'Confirmez le tissu, la coupe et les finitions, et vous obtiendrez un hoodie que vous aurez vraiment envie de porter, pas un simple effet photo.',
      },
      pl: {
        title: 'Bluzy i swetry w arkuszu USFANS: szybki przewodnik zakupowy',
        metaDescription: 'Przewodnik skupiony na kategorii bluz i swetrów w arkuszu USFANS: materiał, krój i detale, które warto sprawdzić przed zamówieniem.',
        excerpt: 'Co sprawdzić w kwestii materiału, kroju i detali przed zakupem bluzy.',
        intro: 'Bluzy i swetry to przede wszystkim wygoda, ale wygoda zależy od detali, których nie zawsze widać na jednym zdjęciu — jak gramatura, krój i jakość nadruku. Kilka celnych pytań robi dużą różnicę.',
        sections: [
          {
            heading: 'Co sprawdzić przed zakupem bluzy',
            paragraph: 'Tkanina i krój to dwie rzeczy, które decydują, czy bluza stanie się ulubioną, czy wyląduje w szufladzie.',
            bullets: [
              'Zapytaj o gramaturę i czy to bawełna, czy mieszanka',
              'Potwierdź krój — luźny, oversize czy zgodny z rozmiarem',
              'Sprawdź jakość nadruku lub haftu na zbliżeniach',
            ],
          },
          {
            heading: 'Detale bluzy, które mają znaczenie',
            paragraph: 'Takie rzeczy jak grubość sznurków, podszewka i szwy wokół kieszeni są drobne, ale wiele mówią o ogólnej jakości.',
          },
        ],
        faq: [
          { q: 'Z jakiego materiału jest bluza?', a: 'Zapytaj sprzedawcę wprost, bo ogłoszenia nie zawsze są precyzyjne. Bawełna, polar i mieszanki noszą się i piorą inaczej.' },
          { q: 'Jak wybrać właściwy krój?', a: 'Potwierdź, czy bluza jest zgodna z rozmiarem, oversize czy dopasowana, i porównaj wymiary z bluzą, którą już masz.' },
          { q: 'Czy sprawdzać jakość nadruku?', a: 'Tak. Poproś o zbliżenie każdego nadruku lub haftu, aby potwierdzić, że wygląda czysto przed zamówieniem.' },
        ],
        conclusion: 'Potwierdź tkaninę, krój i detale wykończenia, a dostaniesz bluzę, którą naprawdę chcesz nosić, a nie taką, która dobrze wygląda tylko na zdjęciach.',
      },
    },
  },
  {
    slug: 'tshirts',
    content: {
      en: {
        title: 'USFANS Spreadsheet T-Shirts: What to Check Before Ordering',
        metaDescription: 'A focused guide to the t-shirts category in the USFANS spreadsheet — fabric, fit and print quality before you order.',
        excerpt: 'What to check about fabric, fit and print quality before buying t-shirts.',
        intro: 'T-shirts look simple, but the difference between a great one and a disappointing one comes down to fabric weight, fit and how well the print holds up. These are all checkable before you commit.',
        sections: [
          {
            heading: 'What to Check Before Buying T-Shirts',
            paragraph: 'A t-shirt is more than a blank canvas — the fabric and cut determine how it feels and how long it lasts.',
            bullets: [
              'Ask about fabric weight and whether it is 100% cotton',
              'Confirm the fit and the shoulder-to-length proportions',
              'Check the print quality and how it is applied',
            ],
          },
          {
            heading: 'Avoiding Common T-Shirt Regrets',
            paragraph: 'The most common regrets are a too-thin fabric and a print that cracks after a few washes. Asking about both up front is worth the time.',
          },
        ],
        faq: [
          { q: 'How thick is the t-shirt fabric?', a: 'Ask the seller for the fabric weight, since thicker cotton generally lasts longer and feels more premium.' },
          { q: 'Will the print crack after washing?', a: 'This depends on the printing method, so ask how the design is applied and follow the care instructions.' },
          { q: 'How do I choose the right size?', a: 'Check the measurements and compare them against a t-shirt you already own, rather than relying on the label alone.' },
        ],
        conclusion: 'Confirm the fabric, fit and print method, and you will avoid the two most common t-shirt disappointments before they happen.',
      },
      es: {
        title: 'Camisetas en el spreadsheet de USFANS: qué revisar antes de pedir',
        metaDescription: 'Una guía centrada en la categoría de camisetas del spreadsheet de USFANS: tejido, ajuste y calidad del estampado antes de pedir.',
        excerpt: 'Qué revisar sobre tejido, ajuste y calidad del estampado antes de comprar camisetas.',
        intro: 'Las camisetas parecen simples, pero la diferencia entre una buena y una decepcionante está en el peso del tejido, el ajuste y cuánto aguanta el estampado. Todo eso se puede comprobar antes de decidir.',
        sections: [
          {
            heading: 'Qué revisar antes de comprar camisetas',
            paragraph: 'Una camiseta es más que un lienzo en blanco: el tejido y el corte determinan cómo sienta y cuánto dura.',
            bullets: [
              'Pregunta por el peso del tejido y si es 100% algodón',
              'Confirma el ajuste y las proporciones de hombro a largo',
              'Revisa la calidad del estampado y cómo se aplica',
            ],
          },
          {
            heading: 'Evitar los arrepentimientos comunes con camisetas',
            paragraph: 'Los arrepentimientos más comunes son un tejido demasiado fino y un estampado que se agrieta tras unos lavados. Preguntar por ambos de antemano vale la pena.',
          },
        ],
        faq: [
          { q: '¿Qué grosor tiene el tejido de la camiseta?', a: 'Pregunta al vendedor por el peso del tejido, ya que el algodón más grueso suele durar más y sentirse más premium.' },
          { q: '¿Se agrietará el estampado al lavar?', a: 'Depende del método de impresión, así que pregunta cómo se aplica el diseño y sigue las instrucciones de cuidado.' },
          { q: '¿Cómo elijo la talla correcta?', a: 'Revisa las medidas y compáralas con una camiseta que ya tengas, en lugar de fiarte solo de la etiqueta.' },
        ],
        conclusion: 'Confirma el tejido, el ajuste y el método de impresión, y evitarás las dos decepciones más comunes con las camisetas antes de que ocurran.',
      },
      fr: {
        title: 'T-shirts dans le spreadsheet USFANS : quoi vérifier avant de commander',
        metaDescription: 'Un guide ciblé sur la catégorie t-shirts du spreadsheet USFANS : tissu, coupe et qualité d\u2019impression avant de commander.',
        excerpt: 'Quoi vérifier sur le tissu, la coupe et la qualité d\u2019impression avant d\u2019acheter des t-shirts.',
        intro: 'Les t-shirts semblent simples, mais la différence entre un bon et un décevant tient au poids du tissu, à la coupe et à la tenue de l\u2019impression. Tout cela se vérifie avant de vous engager.',
        sections: [
          {
            heading: 'Quoi vérifier avant d\u2019acheter des t-shirts',
            paragraph: 'Un t-shirt est plus qu\u2019une toile vierge : le tissu et la coupe déterminent son confort et sa durée de vie.',
            bullets: [
              'Demandez le poids du tissu et s\u2019il est 100% coton',
              'Confirmez la coupe et les proportions épaule-longueur',
              'Vérifiez la qualité de l\u2019impression et sa méthode d\u2019application',
            ],
          },
          {
            heading: 'Éviter les regrets courants avec les t-shirts',
            paragraph: 'Les regrets les plus fréquents sont un tissu trop fin et une impression qui craquelle après quelques lavages. Poser les bonnes questions à l\u2019avance en vaut la peine.',
          },
        ],
        faq: [
          { q: 'Quelle est l\u2019épaisseur du tissu du t-shirt ?', a: 'Demandez le poids du tissu au vendeur, car un coton plus épais dure généralement plus longtemps et fait plus premium.' },
          { q: 'L\u2019impression va-t-elle craqueler au lavage ?', a: 'Cela dépend de la méthode d\u2019impression, donc demandez comment le motif est appliqué et suivez les consignes d\u2019entretien.' },
          { q: 'Comment choisir la bonne taille ?', a: 'Vérifiez les mesures et comparez-les avec un t-shirt que vous possédez déjà, plutôt que de vous fier à l\u2019étiquette seule.' },
        ],
        conclusion: 'Confirmez le tissu, la coupe et la méthode d\u2019impression, et vous éviterez les deux déceptions les plus courantes avec les t-shirts avant qu\u2019elles n\u2019arrivent.',
      },
      pl: {
        title: 'Koszulki w arkuszu USFANS: co sprawdzić przed zamówieniem',
        metaDescription: 'Przewodnik skupiony na kategorii koszulek w arkuszu USFANS: tkanina, krój i jakość nadruku przed zamówieniem.',
        excerpt: 'Co sprawdzić w kwestii tkaniny, kroju i jakości nadruku przed zakupem koszulki.',
        intro: 'Koszulki wydają się proste, ale różnica między dobrą a rozczarowującą sprowadza się do gramatury, kroju i trwałości nadruku. Wszystko to można sprawdzić przed decyzją.',
        sections: [
          {
            heading: 'Co sprawdzić przed zakupem koszulki',
            paragraph: 'Koszulka to więcej niż czyste płótno: tkanina i krój decydują o tym, jak się nosi i jak długo wytrzyma.',
            bullets: [
              'Zapytaj o gramaturę i czy to 100% bawełna',
              'Potwierdź krój i proporcje ramion do długości',
              'Sprawdź jakość nadruku i sposób jego aplikacji',
            ],
          },
          {
            heading: 'Unikanie typowych rozczarowań z koszulkami',
            paragraph: 'Najczęstsze rozczarowania to zbyt cienka tkanina i nadruk, który pęka po kilku praniach. Warto zapytać o obie rzeczy z wyprzedzeniem.',
          },
        ],
        faq: [
          { q: 'Jaka jest grubość tkaniny koszulki?', a: 'Zapytaj sprzedawcę o gramaturę, bo grubsza bawełna zwykle trwa dłużej i sprawia wrażenie bardziej premium.' },
          { q: 'Czy nadruk popęka po praniu?', a: 'To zależy od metody druku, więc zapytaj, jak wzór jest aplikowany, i przestrzegaj instrukcji pielęgnacji.' },
          { q: 'Jak wybrać właściwy rozmiar?', a: 'Sprawdź wymiary i porównaj je z koszulką, którą już masz, zamiast polegać wyłącznie na metce.' },
        ],
        conclusion: 'Potwierdź tkaninę, krój i metodę druku, a unikniesz dwóch najczęstszych rozczarowań z koszulkami, zanim się pojawią.',
      },
    },
  },
  {
    slug: 'jackets',
    content: {
      en: {
        title: 'USFANS Spreadsheet Jackets: What to Check Before You Buy',
        metaDescription: 'A focused guide to the jackets category in the USFANS spreadsheet — material, sizing and the details that matter before ordering.',
        excerpt: 'What to check about material, sizing and details before buying jackets.',
        intro: 'Jackets are usually a bigger investment than a t-shirt, so it pays to check the details carefully. Material, lining, zippers and sizing all affect how a jacket wears and how long it lasts.',
        sections: [
          {
            heading: 'What to Check Before Buying Jackets',
            paragraph: 'A jacket has many components, and each one is worth a quick look before you commit.',
            bullets: [
              'Ask about the outer material and the lining',
              'Confirm the sizing and how the shoulders fit',
              'Check zippers, buttons and stitching in close-ups',
            ],
          },
          {
            heading: 'Details That Reveal Jacket Quality',
            paragraph: 'The hardware — zippers, snaps and buttons — plus the stitching around stress points usually tell you more about quality than the photos alone.',
          },
        ],
        faq: [
          { q: 'What is the jacket made of?', a: 'Ask about both the outer material and the lining, since they affect warmth, weight and how the jacket drapes.' },
          { q: 'How do I get the right jacket size?', a: 'Confirm the shoulder and chest measurements, and compare them against a jacket you already own for a reliable fit.' },
          { q: 'Should I check the zippers and buttons?', a: 'Yes. Close-up photos of the hardware and stitching help you spot quality problems before ordering.' },
        ],
        conclusion: 'A little extra checking on material, sizing and hardware turns the jackets category from a gamble into a confident purchase.',
      },
      es: {
        title: 'Chaquetas en el spreadsheet de USFANS: qué revisar antes de comprar',
        metaDescription: 'Una guía centrada en la categoría de chaquetas del spreadsheet de USFANS: material, tallaje y detalles que importan antes de pedir.',
        excerpt: 'Qué revisar sobre material, tallaje y detalles antes de comprar chaquetas.',
        intro: 'Las chaquetas suelen ser una inversión mayor que una camiseta, así que vale la pena revisar los detalles con cuidado. El material, el forro, las cremalleras y el tallaje afectan cómo se lleva y cuánto dura.',
        sections: [
          {
            heading: 'Qué revisar antes de comprar chaquetas',
            paragraph: 'Una chaqueta tiene muchos componentes, y cada uno merece un vistazo rápido antes de decidirte.',
            bullets: [
              'Pregunta por el material exterior y el forro',
              'Confirma el tallaje y cómo sientan los hombros',
              'Revisa cremalleras, botones y costuras en primeros planos',
            ],
          },
          {
            heading: 'Detalles que revelan la calidad de una chaqueta',
            paragraph: 'Los herrajes — cremalleras, broches y botones — junto con las costuras en los puntos de tensión suelen decir más de la calidad que las fotos por sí solas.',
          },
        ],
        faq: [
          { q: '¿De qué está hecha la chaqueta?', a: 'Pregunta por el material exterior y el forro, ya que afectan el abrigo, el peso y cómo cae la chaqueta.' },
          { q: '¿Cómo consigo la talla correcta de chaqueta?', a: 'Confirma las medidas de hombro y pecho, y compáralas con una chaqueta que ya tengas para un ajuste fiable.' },
          { q: '¿Debo revisar las cremalleras y botones?', a: 'Sí. Las fotos cercanas de los herrajes y las costuras te ayudan a detectar problemas de calidad antes de pedir.' },
        ],
        conclusion: 'Un poco más de revisión sobre material, tallaje y herrajes convierte la categoría de chaquetas de una apuesta en una compra segura.',
      },
      fr: {
        title: 'Vestes dans le spreadsheet USFANS : quoi vérifier avant d\u2019acheter',
        metaDescription: 'Un guide ciblé sur la catégorie vestes du spreadsheet USFANS : matière, taille et détails qui comptent avant de commander.',
        excerpt: 'Quoi vérifier sur la matière, la taille et les détails avant d\u2019acheter des vestes.',
        intro: 'Les vestes sont souvent un investissement plus important qu\u2019un t-shirt, donc il vaut la peine de vérifier les détails avec soin. Matière, doublure, fermetures et taille affectent le port et la durée de vie.',
        sections: [
          {
            heading: 'Quoi vérifier avant d\u2019acheter des vestes',
            paragraph: 'Une veste a de nombreux composants, et chacun mérite un coup d\u2019œil rapide avant de vous engager.',
            bullets: [
              'Demandez la matière extérieure et la doublure',
              'Confirmez la taille et la tenue aux épaules',
              'Vérifiez fermetures, boutons et coutures en gros plan',
            ],
          },
          {
            heading: 'Les détails qui révèlent la qualité d\u2019une veste',
            paragraph: 'Les accessoires — fermetures, pressions et boutons — ainsi que les coutures aux points de tension en disent souvent plus sur la qualité que les seules photos.',
          },
        ],
        faq: [
          { q: 'En quoi est faite la veste ?', a: 'Demandez la matière extérieure et la doublure, car elles influent sur la chaleur, le poids et le tombé de la veste.' },
          { q: 'Comment obtenir la bonne taille de veste ?', a: 'Confirmez les mesures d\u2019épaules et de poitrine, et comparez-les avec une veste que vous possédez déjà pour un ajustement fiable.' },
          { q: 'Dois-je vérifier les fermetures et boutons ?', a: 'Oui. Des photos en gros plan des accessoires et des coutures vous aident à repérer les problèmes de qualité avant de commander.' },
        ],
        conclusion: 'Un peu plus de vérification sur la matière, la taille et les accessoires transforme la catégorie vestes en un achat sûr plutôt qu\u2019un pari.',
      },
      pl: {
        title: 'Kurtki w arkuszu USFANS: co sprawdzić przed zakupem',
        metaDescription: 'Przewodnik skupiony na kategorii kurtek w arkuszu USFANS: materiał, rozmiar i detale, które mają znaczenie przed zamówieniem.',
        excerpt: 'Co sprawdzić w kwestii materiału, rozmiaru i detali przed zakupem kurtki.',
        intro: 'Kurtki to zwykle większa inwestycja niż koszulka, więc warto dokładnie sprawdzić detale. Materiał, podszewka, zamki i rozmiar wpływają na to, jak kurtka się nosi i jak długo wytrzyma.',
        sections: [
          {
            heading: 'Co sprawdzić przed zakupem kurtki',
            paragraph: 'Kurtka ma wiele elementów i każdy z nich zasługuje na szybki rzut oka przed decyzją.',
            bullets: [
              'Zapytaj o materiał wierzchni i podszewkę',
              'Potwierdź rozmiar i to, jak leżą ramiona',
              'Sprawdź zamki, guziki i szwy na zbliżeniach',
            ],
          },
          {
            heading: 'Detale, które zdradzają jakość kurtki',
            paragraph: 'Okucia — zamki, napy i guziki — oraz szwy w punktach naprężenia zwykle mówią więcej o jakości niż same zdjęcia.',
          },
        ],
        faq: [
          { q: 'Z czego jest zrobiona kurtka?', a: 'Zapytaj o materiał wierzchni i podszewkę, bo wpływają na ciepło, wagę i to, jak kurtka się układa.' },
          { q: 'Jak dobrać właściwy rozmiar kurtki?', a: 'Potwierdź wymiary ramion i klatki piersiowej i porównaj je z kurtką, którą już masz, aby uzyskać pewny rozmiar.' },
          { q: 'Czy sprawdzać zamki i guziki?', a: 'Tak. Zbliżenia okuć i szwów pomagają wykryć problemy z jakością przed zamówieniem.' },
        ],
        conclusion: 'Trochę więcej sprawdzenia materiału, rozmiaru i okuć zamienia kategorię kurtek z hazardu w pewny zakup.',
      },
    },
  },
  {
    slug: 'pants',
    content: {
      en: {
        title: 'USFANS Spreadsheet Pants & Shorts: A Quick Buying Guide',
        metaDescription: 'A focused guide to the pants and shorts category in the USFANS spreadsheet — fit, length and fabric before ordering.',
        excerpt: 'What to check about fit, length and fabric before buying pants.',
        intro: 'Pants and shorts are harder to get right than tops because fit depends on several measurements at once. Waist, length and fabric all need to line up for a pair you will actually wear.',
        sections: [
          {
            heading: 'What to Check Before Buying Pants',
            paragraph: 'Getting the fit right means checking more than just the waist size.',
            bullets: [
              'Confirm the waist and inseam measurements',
              'Ask about the fabric and how much it stretches',
              'Check the rise and leg width against your preference',
            ],
          },
          {
            heading: 'Avoiding Fit Mistakes',
            paragraph: 'The most common regret is a pair that fits at the waist but is wrong in length or width. Asking for full measurements up front prevents this.',
          },
        ],
        faq: [
          { q: 'What measurements should I ask for?', a: 'Ask for the waist, inseam, rise and leg width, since pants need all of these to match your body and style.' },
          { q: 'Does the fabric stretch?', a: 'Ask the seller, because stretch affects both comfort and how the pants keep their shape over time.' },
          { q: 'How do I avoid the wrong length?', a: 'Compare the inseam measurement against a pair of pants you already own rather than guessing from the label.' },
        ],
        conclusion: 'Confirm the full set of measurements and the fabric, and the pants and shorts category becomes far easier to get right the first time.',
      },
      es: {
        title: 'Pantalones y pantalones cortos en el spreadsheet de USFANS: guía rápida',
        metaDescription: 'Una guía centrada en la categoría de pantalones y shorts del spreadsheet de USFANS: ajuste, largo y tejido antes de pedir.',
        excerpt: 'Qué revisar sobre ajuste, largo y tejido antes de comprar pantalones.',
        intro: 'Los pantalones y shorts son más difíciles de acertar que las partes de arriba, porque el ajuste depende de varias medidas a la vez. La cintura, el largo y el tejido deben cuadrar para un par que de verdad uses.',
        sections: [
          {
            heading: 'Qué revisar antes de comprar pantalones',
            paragraph: 'Acertar con el ajuste significa revisar más que solo la talla de cintura.',
            bullets: [
              'Confirma las medidas de cintura y entrepierna',
              'Pregunta por el tejido y cuánto estira',
              'Revisa el tiro y el ancho de pierna según tu preferencia',
            ],
          },
          {
            heading: 'Evitar errores de ajuste',
            paragraph: 'El arrepentimiento más común es un par que sienta bien de cintura pero queda mal de largo o ancho. Pedir las medidas completas de antemano lo evita.',
          },
        ],
        faq: [
          { q: '¿Qué medidas debo pedir?', a: 'Pide cintura, entrepierna, tiro y ancho de pierna, ya que los pantalones necesitan que todas encajen con tu cuerpo y estilo.' },
          { q: '¿El tejido estira?', a: 'Pregunta al vendedor, porque el estiramiento afecta tanto la comodidad como el mantenimiento de la forma con el tiempo.' },
          { q: '¿Cómo evito el largo equivocado?', a: 'Compara la medida de entrepierna con un par de pantalones que ya tengas en lugar de adivinar con la etiqueta.' },
        ],
        conclusion: 'Confirma el conjunto completo de medidas y el tejido, y la categoría de pantalones y shorts se vuelve mucho más fácil de acertar a la primera.',
      },
      fr: {
        title: 'Pantalons et shorts dans le spreadsheet USFANS : guide d\u2019achat rapide',
        metaDescription: 'Un guide ciblé sur la catégorie pantalons et shorts du spreadsheet USFANS : coupe, longueur et tissu avant de commander.',
        excerpt: 'Quoi vérifier sur la coupe, la longueur et le tissu avant d\u2019acheter des pantalons.',
        intro: 'Les pantalons et shorts sont plus difficiles à réussir que les hauts, car la coupe dépend de plusieurs mesures à la fois. Taille, longueur et tissu doivent tous correspondre pour une paire que vous porterez vraiment.',
        sections: [
          {
            heading: 'Quoi vérifier avant d\u2019acheter des pantalons',
            paragraph: 'Réussir la coupe signifie vérifier plus que la simple taille.',
            bullets: [
              'Confirmez les mesures de taille et d\u2019entrejambe',
              'Demandez le tissu et son élasticité',
              'Vérifiez la hauteur et la largeur de jambe selon vos préférences',
            ],
          },
          {
            heading: 'Éviter les erreurs de coupe',
            paragraph: 'Le regret le plus courant est une paire qui va à la taille mais pas en longueur ou en largeur. Demander toutes les mesures à l\u2019avance évite cela.',
          },
        ],
        faq: [
          { q: 'Quelles mesures demander ?', a: 'Demandez la taille, l\u2019entrejambe, la hauteur et la largeur de jambe, car un pantalon doit correspondre à votre corps et à votre style sur tous ces points.' },
          { q: 'Le tissu est-il extensible ?', a: 'Demandez au vendeur, car l\u2019élasticité influence le confort et la tenue de la forme dans le temps.' },
          { q: 'Comment éviter la mauvaise longueur ?', a: 'Comparez la mesure d\u2019entrejambe avec un pantalon que vous possédez déjà plutôt que de deviner à partir de l\u2019étiquette.' },
        ],
        conclusion: 'Confirmez l\u2019ensemble des mesures et le tissu, et la catégorie pantalons et shorts devient bien plus facile à réussir du premier coup.',
      },
      pl: {
        title: 'Spodnie i szorty w arkuszu USFANS: szybki przewodnik zakupowy',
        metaDescription: 'Przewodnik skupiony na kategorii spodni i szortów w arkuszu USFANS: krój, długość i tkanina przed zamówieniem.',
        excerpt: 'Co sprawdzić w kwestii kroju, długości i tkaniny przed zakupem spodni.',
        intro: 'Spodnie i szorty trudniej trafić niż górę, bo krój zależy od kilku wymiarów naraz. Talia, długość i tkanina muszą się zgadzać, żeby para była naprawdę noszona.',
        sections: [
          {
            heading: 'Co sprawdzić przed zakupem spodni',
            paragraph: 'Trafienie w krój oznacza sprawdzenie czegoś więcej niż tylko rozmiaru talii.',
            bullets: [
              'Potwierdź wymiary talii i nogawki',
              'Zapytaj o tkaninę i jej rozciągliwość',
              'Sprawdź stan i szerokość nogawki według preferencji',
            ],
          },
          {
            heading: 'Unikanie błędów w kroju',
            paragraph: 'Najczęstszy żal to para, która pasuje w talii, ale ma złą długość lub szerokość. Poproszenie o pełne wymiary z wyprzedzeniem temu zapobiega.',
          },
        ],
        faq: [
          { q: 'O jakie wymiary prosić?', a: 'Poproś o talię, długość nogawki, stan i szerokość nogawki, bo spodnie muszą pasować do Twojej sylwetki i stylu pod każdym z tych względów.' },
          { q: 'Czy tkanina się rozciąga?', a: 'Zapytaj sprzedawcę, bo rozciągliwość wpływa na wygodę i utrzymanie kształtu z czasem.' },
          { q: 'Jak uniknąć złej długości?', a: 'Porównaj długość nogawki z parą spodni, którą już masz, zamiast zgadywać na podstawie metki.' },
        ],
        conclusion: 'Potwierdź pełny zestaw wymiarów i tkaninę, a kategoria spodni i szortów stanie się znacznie łatwiejsza do trafienia za pierwszym razem.',
      },
    },
  },
  {
    slug: 'headwear',
    content: {
      en: {
        title: 'USFANS Spreadsheet Headwear: Caps & Beanies Buying Guide',
        metaDescription: 'A focused guide to the headwear category in the USFANS spreadsheet — fit, material and logo quality for caps and beanies.',
        excerpt: 'What to check about fit, material and logo quality before buying headwear.',
        intro: 'Headwear is small, but the details matter — especially when it comes to fit and how well the logo is applied. A cap or beanie that looks great in photos can feel very different in person.',
        sections: [
          {
            heading: 'What to Check Before Buying Headwear',
            paragraph: 'Fit and finishing are the two things that separate a cap you wear daily from one that sits unused.',
            bullets: [
              'Confirm whether the cap is adjustable or fitted',
              'Ask about the material and the inner band',
              'Check logo embroidery or print in close-up',
            ],
          },
          {
            heading: 'Details That Matter on Headwear',
            paragraph: 'For caps, the brim shape and strap matter; for beanies, the knit and stretch matter most. Ask about the details you care about rather than relying on photos.',
          },
        ],
        faq: [
          { q: 'Is the cap adjustable?', a: 'Ask the seller, since an adjustable strap gives more flexibility than a fitted cap when it comes to sizing.' },
          { q: 'What material is the beanie made of?', a: 'Confirm the knit and stretch, because these determine warmth, comfort and how well the beanie holds its shape.' },
          { q: 'Should I check the logo quality?', a: 'Yes. A close-up of any embroidery or print helps you judge whether it looks clean before you order.' },
        ],
        conclusion: 'Confirm the fit, material and logo finishing, and headwear becomes a small but satisfying part of your wardrobe rather than a guessing game.',
      },
      es: {
        title: 'Gorros y sombreros en el spreadsheet de USFANS: guía de compra',
        metaDescription: 'Una guía centrada en la categoría de gorros y sombreros del spreadsheet de USFANS: ajuste, material y calidad del logo.',
        excerpt: 'Qué revisar sobre ajuste, material y calidad del logo antes de comprar gorros.',
        intro: 'Los gorros son pequeños, pero los detalles importan, sobre todo el ajuste y cómo se aplica el logo. Una gorra o gorro que se ve genial en fotos puede sentirse muy distinto en persona.',
        sections: [
          {
            heading: 'Qué revisar antes de comprar gorros',
            paragraph: 'El ajuste y el acabado son las dos cosas que separan una gorra que usas a diario de una que queda sin usar.',
            bullets: [
              'Confirma si la gorra es ajustable o de talla fija',
              'Pregunta por el material y la banda interior',
              'Revisa el bordado o estampado del logo en primer plano',
            ],
          },
          {
            heading: 'Detalles que importan en los gorros',
            paragraph: 'En las gorras, la forma del ala y la correa importan; en los gorros de punto, el tejido y la elasticidad. Pregunta por los detalles que te importan en lugar de fiarte de las fotos.',
          },
        ],
        faq: [
          { q: '¿La gorra es ajustable?', a: 'Pregunta al vendedor, ya que una correa ajustable da más flexibilidad de talla que una gorra de talla fija.' },
          { q: '¿De qué material es el gorro de punto?', a: 'Confirma el tejido y la elasticidad, porque determinan el abrigo, la comodidad y cómo mantiene la forma.' },
          { q: '¿Debo revisar la calidad del logo?', a: 'Sí. Un primer plano del bordado o estampado te ayuda a juzgar si se ve limpio antes de pedir.' },
        ],
        conclusion: 'Confirma el ajuste, el material y el acabado del logo, y los gorros se vuelven una parte pequeña pero satisfactoria de tu armario en lugar de un juego de adivinanzas.',
      },
      fr: {
        title: 'Couvre-chefs dans le spreadsheet USFANS : guide d\u2019achat casquettes et bonnets',
        metaDescription: 'Un guide ciblé sur la catégorie couvre-chefs du spreadsheet USFANS : ajustement, matière et qualité du logo.',
        excerpt: 'Quoi vérifier sur l\u2019ajustement, la matière et la qualité du logo avant d\u2019acheter.',
        intro: 'Les couvre-chefs sont petits, mais les détails comptent — surtout l\u2019ajustement et l\u2019application du logo. Une casquette ou un bonnet superbe en photo peut être très différent en vrai.',
        sections: [
          {
            heading: 'Quoi vérifier avant d\u2019acheter un couvre-chef',
            paragraph: 'L\u2019ajustement et la finition sont les deux choses qui séparent une casquette portée tous les jours d\u2019une qui reste au placard.',
            bullets: [
              'Confirmez si la casquette est ajustable ou à taille fixe',
              'Demandez la matière et le bandeau intérieur',
              'Vérifiez la broderie ou l\u2019impression du logo en gros plan',
            ],
          },
          {
            heading: 'Les détails qui comptent sur un couvre-chef',
            paragraph: 'Pour les casquettes, la forme de la visière et la sangle comptent ; pour les bonnets, c\u2019est le tricot et l\u2019élasticité. Posez les bonnes questions plutôt que de vous fier aux photos.',
          },
        ],
        faq: [
          { q: 'La casquette est-elle ajustable ?', a: 'Demandez au vendeur, car une sangle ajustable offre plus de souplesse de taille qu\u2019une casquette à taille fixe.' },
          { q: 'De quelle matière est le bonnet ?', a: 'Confirmez le tricot et l\u2019élasticité, car ils déterminent la chaleur, le confort et la tenue de la forme.' },
          { q: 'Dois-je vérifier la qualité du logo ?', a: 'Oui. Un gros plan de la broderie ou de l\u2019impression vous aide à juger si elle est propre avant de commander.' },
        ],
        conclusion: 'Confirmez l\u2019ajustement, la matière et la finition du logo, et les couvre-chefs deviennent un petit ajout satisfaisant plutôt qu\u2019un jeu de devinettes.',
      },
      pl: {
        title: 'Nakrycia głowy w arkuszu USFANS: przewodnik zakupowy czapek',
        metaDescription: 'Przewodnik skupiony na kategorii nakryć głowy w arkuszu USFANS: dopasowanie, materiał i jakość logo.',
        excerpt: 'Co sprawdzić w kwestii dopasowania, materiału i jakości logo przed zakupem.',
        intro: 'Nakrycia głowy są małe, ale detale mają znaczenie — zwłaszcza dopasowanie i sposób aplikacji logo. Czapka, która świetnie wygląda na zdjęciach, może być zupełnie inna na żywo.',
        sections: [
          {
            heading: 'Co sprawdzić przed zakupem nakrycia głowy',
            paragraph: 'Dopasowanie i wykończenie to dwie rzeczy, które odróżniają czapkę noszoną codziennie od leżącej bezużytecznie.',
            bullets: [
              'Potwierdź, czy czapka jest regulowana, czy w stałym rozmiarze',
              'Zapytaj o materiał i wewnętrzną opaskę',
              'Sprawdź haft lub nadruk logo na zbliżeniu',
            ],
          },
          {
            heading: 'Detale, które mają znaczenie w nakryciach głowy',
            paragraph: 'W czapkach z daszkiem liczy się jego kształt i pasek, a w czapkach zimowych — dzianina i rozciągliwość. Pytaj o detale, które Cię interesują, zamiast polegać na zdjęciach.',
          },
        ],
        faq: [
          { q: 'Czy czapka jest regulowana?', a: 'Zapytaj sprzedawcę, bo regulowany pasek daje większą elastyczność rozmiaru niż czapka w stałym rozmiarze.' },
          { q: 'Z jakiego materiału jest czapka zimowa?', a: 'Potwierdź dzianinę i rozciągliwość, bo decydują o cieple, wygodzie i utrzymaniu kształtu.' },
          { q: 'Czy sprawdzać jakość logo?', a: 'Tak. Zbliżenie haftu lub nadruku pomaga ocenić, czy wygląda czysto przed zamówieniem.' },
        ],
        conclusion: 'Potwierdź dopasowanie, materiał i wykończenie logo, a nakrycia głowy staną się małym, ale satysfakcjonującym dodatkiem do garderoby zamiast zgadywanki.',
      },
    },
  },
  {
    slug: 'sets',
    content: {
      en: {
        title: 'USFANS Spreadsheet Sets: Matching Outfits Buying Guide',
        metaDescription: 'A focused guide to the sets category in the USFANS spreadsheet — how matching sets fit, what to check about fabric, and how to buy a set that works.',
        excerpt: 'What to check about fit and fabric before buying a matching set.',
        intro: 'Matching sets are convenient because the pieces are designed to work together, but that only helps if both pieces actually fit. Since you cannot always try before buying, checking measurements carefully matters even more.',
        sections: [
          {
            heading: 'What to Check Before Buying a Set',
            paragraph: 'A set is really two garments, so you need to confirm the fit for both the top and the bottom.',
            bullets: [
              'Check the sizing for both the top and the bottom separately',
              'Confirm the fabric is the same across both pieces',
              'Ask how the pieces are meant to be worn together',
            ],
          },
          {
            heading: 'Avoiding Set Sizing Problems',
            paragraph: 'The biggest mistake is assuming both pieces use the same sizing scale. Always confirm each piece individually, especially if the set is sold as one size.',
          },
        ],
        faq: [
          { q: 'Do both pieces fit the same?', a: 'Not always. Check the sizing for the top and bottom separately, since a set can fit well on one half and poorly on the other.' },
          { q: 'Is the fabric the same for both pieces?', a: 'Ask the seller, because matching colors do not always mean matching fabric, which affects how the set wears.' },
          { q: 'Can I buy the pieces separately?', a: 'This depends on the seller and listing, so confirm before ordering if you only want one piece of the set.' },
        ],
        conclusion: 'Confirm the fit of both pieces and the fabric, and a matching set becomes a genuinely easy outfit instead of a sizing puzzle.',
      },
      es: {
        title: 'Conjuntos en el spreadsheet de USFANS: guía de compra de outfits a juego',
        metaDescription: 'Una guía centrada en la categoría de conjuntos del spreadsheet de USFANS: cómo sientan, qué revisar del tejido y cómo comprar un conjunto que funcione.',
        excerpt: 'Qué revisar sobre ajuste y tejido antes de comprar un conjunto a juego.',
        intro: 'Los conjuntos a juego son cómodos porque las piezas están pensadas para combinarse, pero eso solo ayuda si ambas piezas sientan bien. Como no siempre puedes probarlos antes de comprar, revisar las medidas con cuidado importa aún más.',
        sections: [
          {
            heading: 'Qué revisar antes de comprar un conjunto',
            paragraph: 'Un conjunto son en realidad dos prendas, así que debes confirmar el ajuste de la parte de arriba y la de abajo.',
            bullets: [
              'Revisa la talla de la parte de arriba y la de abajo por separado',
              'Confirma que el tejido sea el mismo en ambas piezas',
              'Pregunta cómo se supone que se combinan las piezas',
            ],
          },
          {
            heading: 'Evitar problemas de talla en los conjuntos',
            paragraph: 'El mayor error es asumir que ambas piezas usan la misma escala de tallas. Confirma siempre cada pieza por separado, sobre todo si el conjunto se vende en talla única.',
          },
        ],
        faq: [
          { q: '¿Ambas piezas sientan igual?', a: 'No siempre. Revisa la talla de la parte de arriba y la de abajo por separado, porque un conjunto puede quedar bien en una mitad y mal en la otra.' },
          { q: '¿El tejido es el mismo en ambas piezas?', a: 'Pregunta al vendedor, porque colores a juego no siempre significan tejido a juego, y eso afecta cómo se lleva el conjunto.' },
          { q: '¿Puedo comprar las piezas por separado?', a: 'Depende del vendedor y del anuncio, así que confírmalo antes de pedir si solo quieres una pieza del conjunto.' },
        ],
        conclusion: 'Confirma el ajuste de ambas piezas y el tejido, y un conjunto a juego se vuelve un outfit realmente fácil en lugar de un rompecabezas de tallas.',
      },
      fr: {
        title: 'Ensembles dans le spreadsheet USFANS : guide d\u2019achat des tenues assorties',
        metaDescription: 'Un guide ciblé sur la catégorie ensembles du spreadsheet USFANS : la coupe, quoi vérifier sur le tissu et comment acheter un ensemble qui fonctionne.',
        excerpt: 'Quoi vérifier sur la coupe et le tissu avant d\u2019acheter un ensemble assorti.',
        intro: 'Les ensembles assortis sont pratiques car les pièces sont conçues pour aller ensemble, mais cela n\u2019aide que si les deux pièces vous vont. Comme on ne peut pas toujours essayer avant d\u2019acheter, vérifier les mesures avec soin compte encore plus.',
        sections: [
          {
            heading: 'Quoi vérifier avant d\u2019acheter un ensemble',
            paragraph: 'Un ensemble, ce sont en réalité deux vêtements, donc vous devez confirmer la coupe du haut et du bas.',
            bullets: [
              'Vérifiez la taille du haut et du bas séparément',
              'Confirmez que le tissu est le même sur les deux pièces',
              'Demandez comment les pièces sont censées se porter ensemble',
            ],
          },
          {
            heading: 'Éviter les problèmes de taille des ensembles',
            paragraph: 'La plus grosse erreur est de supposer que les deux pièces utilisent la même échelle de tailles. Confirmez toujours chaque pièce séparément, surtout si l\u2019ensemble est vendu en taille unique.',
          },
        ],
        faq: [
          { q: 'Les deux pièces taillent-elles pareil ?', a: 'Pas toujours. Vérifiez la taille du haut et du bas séparément, car un ensemble peut bien aller sur une moitié et mal sur l\u2019autre.' },
          { q: 'Le tissu est-il le même sur les deux pièces ?', a: 'Demandez au vendeur, car des couleurs assorties ne signifient pas toujours un tissu assorti, ce qui influence le port de l\u2019ensemble.' },
          { q: 'Puis-je acheter les pièces séparément ?', a: 'Cela dépend du vendeur et de l\u2019annonce, donc confirmez avant de commander si vous ne voulez qu\u2019une pièce de l\u2019ensemble.' },
        ],
        conclusion: 'Confirmez la coupe des deux pièces et le tissu, et un ensemble assorti devient une tenue vraiment facile plutôt qu\u2019un casse-tête de tailles.',
      },
      pl: {
        title: 'Zestawy w arkuszu USFANS: przewodnik zakupowy dopasowanych strojów',
        metaDescription: 'Przewodnik skupiony na kategorii zestawów w arkuszu USFANS: jak leżą, co sprawdzić w tkaninie i jak kupić zestaw, który się sprawdzi.',
        excerpt: 'Co sprawdzić w kwestii dopasowania i tkaniny przed zakupem zestawu.',
        intro: 'Dopasowane zestawy są wygodne, bo elementy są zaprojektowane do łączenia, ale to pomaga tylko wtedy, gdy oba pasują. Ponieważ nie zawsze można przymierzyć przed zakupem, dokładne sprawdzenie wymiarów ma jeszcze większe znaczenie.',
        sections: [
          {
            heading: 'Co sprawdzić przed zakupem zestawu',
            paragraph: 'Zestaw to tak naprawdę dwie rzeczy, więc musisz potwierdzić dopasowanie góry i dołu.',
            bullets: [
              'Sprawdź rozmiar góry i dołu osobno',
              'Potwierdź, że tkanina jest taka sama w obu elementach',
              'Zapytaj, jak elementy mają być noszone razem',
            ],
          },
          {
            heading: 'Unikanie problemów z rozmiarem zestawów',
            paragraph: 'Największy błąd to zakładanie, że oba elementy używają tej samej skali rozmiarów. Zawsze potwierdzaj każdy element osobno, zwłaszcza gdy zestaw jest sprzedawany w jednym rozmiarze.',
          },
        ],
        faq: [
          { q: 'Czy oba elementy leżą tak samo?', a: 'Nie zawsze. Sprawdź rozmiar góry i dołu osobno, bo zestaw może dobrze leżeć na jednej połowie, a źle na drugiej.' },
          { q: 'Czy tkanina jest taka sama w obu elementach?', a: 'Zapytaj sprzedawcę, bo dopasowane kolory nie zawsze oznaczają dopasowaną tkaninę, co wpływa na noszenie zestawu.' },
          { q: 'Czy mogę kupić elementy osobno?', a: 'To zależy od sprzedawcy i ogłoszenia, więc potwierdź przed zamówieniem, jeśli chcesz tylko jeden element zestawu.' },
        ],
        conclusion: 'Potwierdź dopasowanie obu elementów i tkaninę, a dopasowany zestaw stanie się naprawdę łatwym strojem zamiast łamigłówki z rozmiarami.',
      },
    },
  },
];